import React, { useState } from 'react';
import { useAuth } from '../context/AuthContext';

type ViewType = 'home' | 'profile' | 'notifications' | 'messages' | 'collections' | 'subscriptions' | 'settings' | 'analytics' | 'creator-application' | 'admin-dashboard' | 'kyc-admin';

interface BottomNavigationProps {
    activeView?: ViewType;
    onViewChange?: (view: ViewType) => void;
    onCreatePost?: () => void;
}

const BottomNavigation: React.FC<BottomNavigationProps> = ({ 
    activeView = 'home', 
    onViewChange,
    onCreatePost 
}) => {
    const [showMoreMenu, setShowMoreMenu] = useState(false);
    const { user } = useAuth();

    const handleViewChange = (view: ViewType) => {
        if (onViewChange) {
            onViewChange(view);
        }
        setShowMoreMenu(false);
    };

    const handleCreatePost = () => {
        if (onCreatePost) {
            onCreatePost();
        } else {
            // Fallback to event dispatch for backward compatibility
            const event = new Event('openCreatePost');
            window.dispatchEvent(event);
        }
    };

    const mainNavItems = [
        { key: 'home', icon: 'feather-home', label: 'Home' },
        ...(user?.role === 'creator' ? [{ key: 'notifications', icon: 'feather-bell', label: 'Alerts' }] : []),
        { key: 'messages', icon: 'feather-message-circle', label: 'Chat' },
        { key: 'profile', icon: 'feather-user', label: 'Profile' }
    ];

    const moreNavItems = [
        { key: 'collections', icon: 'feather-bookmark', label: 'Collections' },
        { key: 'subscriptions', icon: 'feather-credit-card', label: 'Subscriptions' },
        { key: 'creator-application', icon: 'feather-plus-circle', label: 'Creator App' },
        { key: 'analytics', icon: 'feather-bar-chart', label: 'Analytics' },
        { key: 'settings', icon: 'feather-settings', label: 'Settings' }
    ];

    // Add admin dashboard for admin users
    const allMoreNavItems = user?.role === 'admin'
        ? [...moreNavItems, { key: 'admin-dashboard', icon: 'feather-shield', label: 'Admin' }]
        : moreNavItems;

    return (
        <>
            {/* Main Bottom Navigation */}
            <div className="fixed-bottom d-lg-none bg-white border-top shadow-sm" style={{ zIndex: 1000 }}>
                <div className="container-fluid">
                    <div className="row text-center py-2">
                        {mainNavItems.map((item) => (
                            <div key={item.key} className="col">
                                <button
                                    className={`d-flex flex-column align-items-center text-decoration-none py-1 border-0 bg-transparent w-100 ${
                                        activeView === item.key ? 'text-primary' : 'text-grey-500'
                                    }`}
                                    onClick={() => handleViewChange(item.key as ViewType)}
                                >
                                    <i className={`${item.icon} font-md mb-1 ${
                                        activeView === item.key ? 'text-primary' : 'text-grey-500'
                                    }`}></i>
                                    <span className={`font-xsss fw-600 ${
                                        activeView === item.key ? 'text-primary' : 'text-grey-500'
                                    }`}>{item.label}</span>
                                </button>
                            </div>
                        ))}

                        {/* Create Post Button */}
                        <div className="col">
                            <button 
                                className="d-flex flex-column align-items-center text-decoration-none py-1 border-0 bg-transparent w-100"
                                onClick={handleCreatePost}
                            >
                                <div className="bg-primary rounded-circle d-flex align-items-center justify-content-center mb-1" 
                                     style={{ width: '32px', height: '32px' }}>
                                    <i className="feather-plus text-white font-sm"></i>
                                </div>
                                <span className="font-xsss fw-600 text-primary">Post</span>
                            </button>
                        </div>

                        {/* More Menu Button */}
                        <div className="col">
                            <button
                                className="d-flex flex-column align-items-center text-decoration-none py-1 border-0 bg-transparent w-100 text-grey-500"
                                onClick={() => setShowMoreMenu(!showMoreMenu)}
                            >
                                <i className="feather-more-horizontal font-md mb-1"></i>
                                <span className="font-xsss fw-600">More</span>
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            {/* More Menu Overlay */}
            {showMoreMenu && (
                <div 
                    className="fixed-bottom d-lg-none bg-white border-top shadow-sm" 
                    style={{ zIndex: 1001, bottom: '70px' }}
                >
                    <div className="container-fluid">
                        <div className="p-3">
                            <div className="d-flex justify-content-between align-items-center mb-3">
                                <h6 className="fw-600 mb-0">More Options</h6>
                                <button 
                                    className="btn btn-sm btn-light rounded-circle p-1"
                                    onClick={() => setShowMoreMenu(false)}
                                >
                                    <i className="feather-x font-xs"></i>
                                </button>
                            </div>
                            <div className="row">
                                {allMoreNavItems.map((item) => (
                                    <div key={item.key} className="col-6 mb-3">
                                        <button
                                            className={`d-flex align-items-center text-decoration-none py-2 px-3 border-0 bg-transparent w-100 rounded-3 ${
                                                activeView === item.key ? 'bg-lightblue text-primary' : 'text-grey-700'
                                            }`}
                                            onClick={() => handleViewChange(item.key as ViewType)}
                                            style={{ textAlign: 'left' }}
                                        >
                                            <i className={`${item.icon} font-md me-3 ${
                                                activeView === item.key ? 'text-primary' : 'text-grey-700'
                                            }`}></i>
                                            <span className={`font-xsss fw-600 ${
                                                activeView === item.key ? 'text-primary' : 'text-grey-700'
                                            }`}>{item.label}</span>
                                        </button>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Backdrop for More Menu */}
            {showMoreMenu && (
                <div 
                    className="position-fixed top-0 start-0 w-100 h-100 d-lg-none"
                    style={{ zIndex: 999, backgroundColor: 'rgba(0,0,0,0.3)' }}
                    onClick={() => setShowMoreMenu(false)}
                ></div>
            )}
        </>
    );
};

export default BottomNavigation;
